import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api } from "@/lib/api";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete the form.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", {
        name: form.name,
        email: form.email,
        message: form.message,
      });
      toast.success("Message sent. Talk soon.");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-32 lg:py-40 border-t border-white/5"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <p className="text-xs uppercase tracking-[0.35em] text-amber-500 mb-6">
              — Or just drop a line
            </p>
            <h2 className="font-serif-display text-4xl sm:text-5xl text-white tracking-tight">
              Not ready to book? <em className="italic text-amber-400">Write to me.</em>
            </h2>
            <p className="mt-6 text-zinc-400">
              Questions about pricing, services, or just getting started? Write
              to me directly — I read every message myself.
            </p>

            <div className="mt-10 space-y-4 text-zinc-300 text-sm">
              <div>
                <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">
                  Email
                </div>
                <a
                  href="mailto:leonardobianchi08@icloud.com"
                  data-testid="contact-email-link"
                  className="mt-1 block font-serif-display text-xl text-white hover:text-amber-400 transition-colors"
                >
                  leonardobianchi08@icloud.com
                </a>
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">
                  Phone
                </div>
                <a
                  href="tel:+13864058006"
                  data-testid="contact-phone-link"
                  className="mt-1 block font-serif-display text-xl text-white hover:text-amber-400 transition-colors"
                >
                  386-405-8006
                </a>
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">
                  Response time
                </div>
                <div className="mt-1 font-serif-display text-xl text-white">
                  Usually within 24 hours
                </div>
              </div>
            </div>
          </div>

          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="lg:col-span-7 rounded-3xl bg-[#0A0A0A] border border-white/10 p-8 lg:p-10 space-y-5"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="c-name" className="text-zinc-300">
                  Name
                </Label>
                <Input
                  id="c-name"
                  data-testid="contact-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  className="mt-2 bg-black/40 border-white/15 text-white h-12 rounded-xl"
                />
              </div>
              <div>
                <Label htmlFor="c-email" className="text-zinc-300">
                  Email
                </Label>
                <Input
                  id="c-email"
                  type="email"
                  data-testid="contact-email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  className="mt-2 bg-black/40 border-white/15 text-white h-12 rounded-xl"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="c-message" className="text-zinc-300">
                Message
              </Label>
              <Textarea
                id="c-message"
                rows={6}
                data-testid="contact-message"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                required
                className="mt-2 bg-black/40 border-white/15 text-white rounded-xl"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="contact-submit"
              className="bg-white text-black hover:bg-zinc-200 rounded-full px-8 py-6"
            >
              {loading ? "Sending..." : "Send message →"}
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
};
